import { useState, useEffect, useCallback } from 'react';
import supabase from '../../supabase-client';
/**
 * Hook personalizado para gestionar los autores en Supabase.
 * Proporciona el estado de los autores, carga, errores y funciones CRUD.
 */
export const useAuthors = () => {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Obtiene la lista de autores desde la base de datos usando SQL directo.
   */
  const fetchAuthors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.rpc('get_all_authors');

      if (error) throw error;

      // Mapear los datos para que coincidan con el formato esperado
      const mappedData = (data || []).map(item => ({
        id_author: item.author_id,
        name_author: item.author_name
      }));

      setAuthors(mappedData);
    } catch (err) {
      console.error('Error fetching authors:', err);
      setError('No se pudieron cargar los autores.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAuthors();
  }, [fetchAuthors]);

  /**
   * Crea un nuevo autor usando SQL directo.
   * @param {object} authorData - Los datos del autor a crear (ej: { name_author: 'Nombre' }).
   */
  const addAuthor = async (authorData) => {
    try {
      setError(null);

      const name = authorData.name_author?.trim();
      if (!name) throw new Error('El nombre del autor es requerido');

      const { data, error } = await supabase.rpc('create_author', {
        p_author_name: name
      });

      if (error) throw new Error(error.message);
      if (!data) throw new Error('No se pudo crear el autor');

      // Recargar autores para mantener sincronización
      await fetchAuthors();
      return data;
    } catch (err) {
      console.error('Error creating author:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * Actualiza un autor existente usando SQL directo.
   * @param {number} id - El ID del autor a actualizar.
   * @param {object} updatedData - Los nuevos datos para el autor.
   */
  const updateAuthor = async (id, updatedData) => {
    try {
      setError(null);

      const name = updatedData.name_author?.trim();
      if (!name) throw new Error('El nombre del autor es requerido');

      const { data, error } = await supabase.rpc('update_author', {
        p_author_id: id,
        p_new_name: name
      });

      if (error) throw new Error(error.message);
      if (!data) throw new Error('No se pudo actualizar el autor');

      // Recargar autores para mantener sincronización
      await fetchAuthors();
      return data;
    } catch (err) {
      console.error('Error updating author:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * Elimina un autor usando SQL directo.
   * @param {number} authorId - El ID del autor a eliminar.
   * @param {string} authorName - El nombre del autor a eliminar.
   */
  const deleteAuthor = async (authorId, authorName) => {
    try {
      // Verificar directamente si el autor está siendo usado por algún libro
      const { data: booksUsingAuthor, error: depError } = await supabase
        .from('productbook')
        .select('title_product')
        .eq('author_id', authorId);

      if (depError) throw new Error(depError.message);

      const isUsed = booksUsingAuthor && booksUsingAuthor.length > 0;
      const bookTitles = booksUsingAuthor?.map(book => book.title_product) || [];

      if (isUsed && bookTitles.length > 0) {
        // Mostrar modal de error si está siendo usado
        return new Promise((resolve, reject) => {
          const modalContainer = document.createElement('div');
          modalContainer.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4';
          
          modalContainer.innerHTML = `
            <div class="bg-white rounded-lg shadow-lg border border-gray-300 w-full max-w-md p-6">
              <div class="flex justify-center mb-4">
                <img src="/assets/images/favicon.png" alt="Logo" class="w-12 h-12" onerror="this.src='/src/assets/favicon.png'"/>
              </div>
              <div class="text-center mb-6">
                <p class="text-gray-700 mb-2">No se puede eliminar el autor</p>
                <p class="font-medium text-gray-900 mb-2">"${authorName}"</p>
                <p class="text-gray-600 text-sm mb-3">El libro "${bookTitles[0]}" lo está usando.</p>
                <p class="text-gray-600 text-sm">Debe quitarlo del libro primero.</p>
              </div>
              <div class="flex justify-center">
                <button id="ok-btn" class="px-6 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors">
                  Entendido
                </button>
              </div>
            </div>
          `;
          
          document.body.appendChild(modalContainer);
          
          const okBtn = modalContainer.querySelector('#ok-btn');
          
          okBtn.onclick = () => {
            document.body.removeChild(modalContainer);
            reject(new Error('No se puede eliminar: autor en uso'));
          };
        });
      }

      // Si no está siendo usado, mostrar confirmación normal
      return new Promise((resolve, reject) => {
        const modalContainer = document.createElement('div');
        modalContainer.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4';
        
        modalContainer.innerHTML = `
          <div class="bg-white rounded-lg shadow-lg border border-gray-300 w-full max-w-md p-6">
            <div class="flex justify-center mb-4">
              <img src="/assets/images/favicon.png" alt="Logo" class="w-12 h-12" onerror="this.src='/src/assets/favicon.png'"/>
            </div>
            <div class="text-center mb-6">
              <p class="text-gray-700 mb-2">¿Quiere eliminar el autor</p>
              <p class="font-medium text-gray-900 mb-2">"${authorName}"?</p>
            </div>
            <div class="flex gap-3 justify-center">
              <button id="cancel-btn" class="px-6 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button id="confirm-btn" class="px-6 py-2 text-white bg-red-600 rounded hover:bg-red-700 transition-colors">
                Aceptar
              </button>
            </div>
          </div>
        `;
        
        document.body.appendChild(modalContainer);
        
        const cancelBtn = modalContainer.querySelector('#cancel-btn');
        const confirmBtn = modalContainer.querySelector('#confirm-btn');
        
        const cleanup = () => {
          document.body.removeChild(modalContainer);
        };
        
        cancelBtn.onclick = () => {
          cleanup();
          reject(new Error('Eliminación cancelada'));
        };
        
        confirmBtn.onclick = async () => {
          confirmBtn.textContent = 'Eliminando...';
          confirmBtn.disabled = true;
          
          try {
            setError(null);
            
            const { data, error } = await supabase.rpc('delete_author', {
              p_author_id: authorId
            });

            if (error) throw new Error(error.message);
            if (!data) throw new Error('No se pudo eliminar el autor');

            await fetchAuthors();
            cleanup();
            resolve(true);
          } catch (err) {
            console.error('Error deleting author:', err);
            setError(err.message);
            cleanup();
            reject(err);
          }
        };
      });
    } catch (err) {
      console.error('Error checking author dependencies:', err);
      setError(err.message);
      throw err;
    }
  };

  return {
    authors,
    loading,
    error,
    addAuthor,
    updateAuthor,
    deleteAuthor,
  };
};
export default useAuthors;
